import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";

import { toasts } from "../components/AppToast";
import { FiCheckCircle, FiGlobe, FiLayers } from "react-icons/fi";

export default function TranslateMLInformation() {
  
  // STATE & AUTH
  
  const [sideOpen, setSideOpen] = useState(true);
  const [activePage, setActivePage] = useState("ML Information");
  const [user, setUser] = useState(null);

  const [sections, setSections] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [selectedSection, setSelectedSection] = useState("1");
  const [selectedLanguage, setSelectedLanguage] = useState("1");

  const [languageName, setLanguageName] = useState("English");
  const [sectionName, setSectionName] = useState("Section");

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  
  // AUTH EFFECT
  
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      return;
    }
    setUser(JSON.parse(localStorage.getItem("user") || "null"));
  }, []);

  
  // FETCH SECTIONS
  
  const fetchSections = useCallback(async () => {
    try {
      const response = await API.get("/manage-lanaguage/ml-translate/sections");

      if (response.data?.success) {
        const fetchedSections = response.data.data || [];
        setSections(fetchedSections);

        const current = fetchedSections.find(
          (s) => String(s.mlsec_id) === String(selectedSection)
        );
        if (current) {
          setSectionName(current.ml_section);
        }
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error("Fetch sections error:", error);
      toasts.error(error.response?.data?.message || "Unable to fetch sections");
    }
  }, [selectedSection]);

  
  // FETCH LANGUAGES
  
  const fetchLanguages = useCallback(async () => {
    try {
      const response = await API.get("/languages");

      if (response.data?.success) {
        setLanguages(response.data.data || []);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error("Fetch languages error:", error);
      toasts.error(error.response?.data?.message || "Unable to fetch languages");
    }
  }, []);

  
  // FETCH TRANSLATION INFORMATION
  
  const fetchMLInformation = useCallback(async () => {
    if (!selectedSection || !selectedLanguage) {
      return;
    }

    try {
      setLoading(true);

      const response = await API.get("/manage-lanaguage/ml-translate", {
        params: {
          modulelist: selectedSection,
          langid: selectedLanguage,
        },
      });

      if (response.data?.success) {
        setData(response.data.data || []);
        setLanguageName(response.data.language?.lang_name || "English");

        if (response.data.section?.ml_section) {
          setSectionName(response.data.section.ml_section);
        } else {
          const matched = sections.find(
            (s) => String(s.mlsec_id) === String(selectedSection)
          );
          if (matched) {
            setSectionName(matched.ml_section);
          }
        }
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Fetch ML information error:", error);
      setData([]);
      toasts.error(
        error.response?.data?.message || "Unable to fetch ML information"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedSection, selectedLanguage, sections]);

  
  // INITIAL LOAD
  
  useEffect(() => {
    fetchSections();
    fetchLanguages();
  }, [fetchSections, fetchLanguages]);

  
  // FETCH DATA WHEN SECTION / LANGUAGE CHANGES
  
  useEffect(() => {
    fetchMLInformation();
  }, [fetchMLInformation]);

  
  // SECTION CHANGE
  
  const handleSectionChange = (e) => {
    const value = e.target.value;
    setSelectedSection(value);

    const matched = sections.find((s) => String(s.mlsec_id) === String(value));
    if (matched) {
      setSectionName(matched.ml_section);
    }
  };

  
  // LANGUAGE CHANGE
  
  const handleLanguageChange = (e) => {
    const value = e.target.value;
    setSelectedLanguage(value);
  };

  
  // TRANSLATION VALUE CHANGE
  
  const handleTranslationChange = (index, value) => {
    setData((prevData) =>
      prevData.map((item, i) =>
        i === index
          ? {
              ...item,
              translated_value: value,
            }
          : item
      )
    );
  };

  
  // SUBMIT TRANSLATIONS
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSection) {
      toasts.warning("Section is required");
      return;
    }

    if (!selectedLanguage) {
      toasts.warning("Language is required");
      return;
    }

    if (data.length === 0) {
      toasts.warning("No information available to translate");
      return;
    }

    try {
      setSaving(true);

      const translations = data.map((item) => ({
        mlsec_id: item.mlsec_id,
        value: item.translated_value || "",
      }));

      const response = await API.put("/manage-lanaguage/ml-translate/save", {
        lang_id: Number(selectedLanguage),
        modulelist: Number(selectedSection),
        translations,
      });

      if (response.data?.success) {
        toasts.success(
          response.data.message || "Translation updated successfully"
        );
        await fetchMLInformation();
      } else {
        toasts.error(response.data?.message || "Unable to update translation");
      }
    } catch (error) {
      console.error("Save translation error:", error);
      toasts.error(
        error.response?.data?.message || "Unable to update translation"
      );
    } finally {
      setSaving(false);
    }
  };

  
  // CLEAR TRANSLATION VALUES
  
  // const handleClear = () => {
  //   setData((prev) =>
  //     prev.map((item) => ({
  //       ...item,
  //       translated_value: "",
  //     }))
  //   );
  // };

  
  // JSX
  
  return (
    <Layout
      sideOpen={sideOpen}
      setSideOpen={setSideOpen}
      activePage={activePage}
      setActivePage={setActivePage}
      user={user}
    >
      <main className="dashboard-main">
        <div className="user-form-page">
          {/* HEADER */}
          <div className="user-form-header">
            <div className="d-flex align-items-center gap-2">
              <div
                className="form-card-icon"
                style={{
                  color: "#0C807C",
                  background: "rgba(12, 128, 124, 0.1)",
                }}
              >
                <FiGlobe size={24} color="#0C807C" />
              </div>
              <div>
                <h2 className="um-modal-title mb-0">Static Informations</h2>
                <p className="form-card-sub mb-0">
                  (Manage multilingual translations and partitioned equivalents)
                </p>
              </div>
            </div>
          </div>

          {/* FORM CARD */}
          <div className="user-form-card">
            <form
              name="frmmlinfos"
              id="frmmlinfos"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              {/* CONFIGURATION SETTINGS */}
              <div className="sec-div">
                <span>Configuration Settings</span>
              </div>

              <div className="row g-4">
                {/* SECTION NAME */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label htmlFor="cmbsection" className="um-label">
                    Section Name <span className="text-danger">*</span>
                  </label>
                  <select
                    name="cmbsection"
                    id="cmbsection"
                    className="um-input"
                    value={selectedSection}
                    onChange={handleSectionChange}
                    disabled={loading || saving}
                  >
                    <option value="">Select Section</option>
                    {sections.map((section) => (
                      <option key={section.mlsec_id} value={section.mlsec_id}>
                        {section.ml_section}
                      </option>
                    ))}
                  </select>
                </div>

                {/* LANGUAGE */}
                <div className="col-xl-6 col-lg-6 col-md-6">
                  <label htmlFor="cmbLanguage" className="um-label">
                    Language <span className="text-danger">*</span>
                  </label>
                  <select
                    name="cmbLanguage"
                    id="cmbLanguage"
                    className="um-input"
                    value={selectedLanguage}
                    onChange={handleLanguageChange}
                    disabled={loading || saving}
                  >
                    <option value="">Select Language</option>
                    {languages.map((language) => (
                      <option key={language.lang_id} value={language.lang_id}>
                        {language.lang_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* TRANSLATION MAIN TITLE */}
              <div className="sec-div mt-4">
                <span>Translate From English To {languageName}</span>
              </div>

              {/* LOADING STATE */}
              {loading && (
                <div className="text-center py-5">
                  <span className="spinner" />
                  <div className="mt-2 text-muted">Loading information...</div>
                </div>
              )}

              {/* NO DATA STATE */}
              {!loading && data.length === 0 && (
                <div className="alert alert-info">
                  No information found for the selected section.
                </div>
              )}

              {/* SECTION-BASED PARTITIONED TRANSLATION LIST */}
              {!loading && data.length > 0 && (
                <>
                  {/* DYNAMIC SECTION NAME PARTITION HEADER WITH #0C807C THEME AND COUNT BADGE */}
                  <div
                    className="d-flex align-items-center justify-content-between mb-3 p-3 rounded border"
                    style={{
                      backgroundColor: "rgba(12, 128, 124, 0.04)",
                      borderColor: "rgba(12, 128, 124, 0.2)",
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FiLayers size={18} style={{ color: "#0C807C" }} />
                      <span className="fw-bold" style={{ color: "#0C807C" }}>
                        Section Name: {sectionName}
                      </span>
                    </div>
                    <span
                      className="badge px-3 py-2 rounded-pill shadow-sm"
                      style={{
                        backgroundColor: "#0C807C",
                        color: "#ffffff",
                        fontSize: "0.85rem",
                        fontWeight: "600",
                      }}
                    >
                      {data.length} {data.length === 1 ? "Item" : "Items"}
                    </span>
                  </div>
                  <div className="text-muted small mb-3">
                    Enter the equivalent text for each information item under
                    the selected section below.
                  </div>
                  {/* UNIFIED TABLE HEADER */}
                  <div className="sec-div mt-3 mb-3">
                    <div className="row w-100 m-0">
                      <div className="col-md-5">
                        <span>ENGLISH EQUIVALENT *</span>
                      </div>
                      <div className="col-md-7">
                        <span>{languageName.toUpperCase()} EQUIVALENT *</span>
                      </div>
                    </div>
                  </div>

                  <div className="row g-4">
                    {data.map((row, index) => (
                      <div
                        className="col-12"
                        key={row.mlsecval_id || `${row.mlsec_id}-${index}`}
                      >
                        <div className="p-3 border rounded bg-light">
                          <div className="row g-3">
                            {/* English */}
                            <div className="col-md-5">
                              <input
                                id={`english-${index}`}
                                type="text"
                                className="um-input bg-white"
                                value={row.english_value || ""}
                                readOnly
                              />

                              <input
                                type="hidden"
                                name="hidsecid[]"
                                value={row.mlsec_id || ""}
                              />
                            </div>

                            {/* Translation */}
                            <div className="col-md-7">
                              <input
                                id={`translation-${index}`}
                                name={`txtsecval[${index}]`}
                                type="text"
                                className="um-input"
                                maxLength={500}
                                value={row.translated_value ?? ""}
                                onChange={(e) =>
                                  handleTranslationChange(index, e.target.value)
                                }
                                placeholder={`Enter ${languageName} equivalent`}
                                disabled={saving}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* FORM FOOTER BUTTONS */}
              {!loading && data.length > 0 && (
                <div className="user-form-footer mt-4">
                  {/* <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={handleClear}
                    disabled={saving}
                  >
                    <FiX />
                    Clear
                  </button> */}

                  <button
                    type="submit"
                    className="um-btn-primary"
                    disabled={saving}
                    style={{
                      backgroundColor: "#0C807C",
                      borderColor: "#0C807C",
                    }}
                  >
                    {saving ? (
                      <>
                        <span className="spinner" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle />
                        Submit Translations
                      </>
                    )}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </main>

   
    </Layout>
  );
}
